import {
  json,
  redirect,
  type LoaderFunctionArgs,
  type SerializeFrom,
} from "@remix-run/node";
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useOutlet,
  useSearchParams,
} from "@remix-run/react";
import {
  IconAlertTriangle,
  IconArrowUpRight,
  IconBriefcase,
  IconFiltersFilled,
  IconMicrophone,
  IconPaperclip,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { motion, type Variants } from "framer-motion";
import i18next from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ChatComposer,
  ChatComposerAction,
  ChatComposerSend,
} from "#app/components/chat/chat-composer";
import { ChatLandingHero } from "#app/components/chat/chat-landing";
import Footer from "#app/components/footer.tsx";
import { LandingHeader } from "#app/components/landing/landing-header";
import { resolveMarketplaceListingInstallType } from "#app/components/marketplace/install-type-badge";
import EmptyState from "#app/components/new-ui/empty-state";
import {
  MarketplaceListingCard,
  marketplaceVersionServiceNames,
  type MarketplaceListingCardData,
} from "#app/components/new-ui/marketplace-listing-card";
import { PaginationBlock } from "#app/components/new-ui/pagination-block";
import { Button } from "#app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#app/components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "#app/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "#app/components/ui/popover";
import { Separator } from "#app/components/ui/separator.tsx";
import { Sheet, SheetContent, SheetTrigger } from "#app/components/ui/sheet";
import {
  SimpleTooltip,
  SimpleTooltip as Tooltip,
} from "#app/components/ui/tooltip";
import { track } from "#app/utils/amplitude.client";
import { getUserId } from "#app/utils/auth.server";
import { ProviderConnectionForm } from "#app/utils/connections.tsx";
import {
  getMarketplaceCategoryTranslationKey,
  getMarketplaceSubcategoryTranslationKey,
  getMarketplaceTagTranslationKey,
  isValidMarketplaceCategoryValue,
} from "#app/utils/marketplace/category-display";
import { truncateMarketplaceDiscoveryLabel } from "#app/utils/marketplace/discovery-prompts";
import {
  getMarketplaceCatalog,
  getMarketplaceCatalogFacets,
  getMarketplaceDiscoveryPrompts,
} from "#app/utils/marketplace/listing.server";
import {
  type MarketplaceOperatorLevelValue,
  isMarketplaceOperatorLevel,
  parseOperatorLevelsFromSearchParams,
} from "#app/utils/marketplace/operator-level.shared";
import { getUserImgSrc, cn } from "#app/utils/misc.tsx";

export const handle = {
  // `listings` supplies the install-type labels on each card.
  i18n: ["marketing", "footer", "common", "listings", "chat", "nav"],
  breadcrumb: () => i18next.t("marketplace", { ns: "nav" }),
  title: () => i18next.t("marketplace", { ns: "nav" }),
};

function getOperatorLevelIconSrc(level: string | null | undefined) {
  switch (level) {
    case "intern":
      return "/img/intern.svg";
    case "director":
      return "/img/director.svg";
    case "manager":
      return "/img/manager.svg";
    case "professional":
    default:
      return "/img/professional.svg";
  }
}

function publisherAvatarInitials(
  publisher:
    | { name?: string | null; username?: string | null }
    | null
    | undefined,
) {
  if (!publisher) return "?";
  const name = publisher.name?.trim();
  if (name) {
    const letter = name.charAt(0);
    if (letter) return letter.toUpperCase();
  }
  const username = publisher.username?.trim();
  if (username) return username.charAt(0).toUpperCase();
  return "?";
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const userId = await getUserId(request);
  if (userId) {
    const url = new URL(request.url);
    // Preserve the full path (including slug) + search when redirecting logged-in users.
    // e.g. /marketplace/some-slug?onboard=true → /u/marketplace/some-slug?onboard=true
    const restOfPath = url.pathname.replace(/^\/marketplace/, "");
    throw redirect(`/u/marketplace${restOfPath}${url.search}`);
  }
  const url = new URL(request.url);
  const page = Number(url.searchParams.get("page") ?? "1") || 1;
  const limit = Number(url.searchParams.get("limit") ?? "12") || 12;
  const search = url.searchParams.get("search") ?? "";
  const categorySlugs = url.searchParams
    .getAll("category")
    .map((s) => s.trim())
    .filter(Boolean);
  const operatorLevels = parseOperatorLevelsFromSearchParams(url.searchParams);
  const employeePackOnly = url.searchParams.get("employeePack") === "1";

  const [catalog, facets, discoveryPrompts] = await Promise.all([
    getMarketplaceCatalog({
      page,
      limit,
      search,
      categorySlugs,
      operatorLevels,
      employeePackOnly,
    }),
    getMarketplaceCatalogFacets(search),
    getMarketplaceDiscoveryPrompts({
      search,
      categorySlugs,
      operatorLevels,
      employeePackOnly,
    }),
  ]);

  return json({
    ...catalog,
    facets: {
      ...facets,
      categories: facets.categories.filter((facet) =>
        isValidMarketplaceCategoryValue(facet.name),
      ),
    },
    discoveryPrompts: discoveryPrompts.filter(isValidMarketplaceCategoryValue),
  });
};

type MarketplaceListing = SerializeFrom<typeof loader>["listings"][number];

/**
 * Flattens a catalog row into the shape `<MarketplaceListingCard>` renders.
 *
 * Kept in step with the in-app catalog's mapper in `app/routes/u+/marketplace.tsx`
 * — same card, same fields. The two loaders differ (this one is public and has no
 * per-user favourites), so the mapping stays per-route while the markup is shared.
 */
function toListingCardData(
  listing: MarketplaceListing,
  t: (key: string) => string,
): MarketplaceListingCardData | null {
  const version = listing.currentPublishedVersion;
  if (!version) return null;

  const display = (
    value: string | null | undefined,
    toKey: (value: string) => string | null | undefined,
  ) => {
    if (!isValidMarketplaceCategoryValue(value)) return null;
    const key = toKey(value);
    return key ? t(key) : value;
  };

  const tags = [
    display(version.category?.name, getMarketplaceCategoryTranslationKey),
    display(version.subCategory?.name, getMarketplaceSubcategoryTranslationKey),
    ...version.tags.map((tag) => {
      const key = getMarketplaceTagTranslationKey(tag);
      return key ? t(key) : tag;
    }),
  ].filter((tag): tag is string => Boolean(tag));

  const level = version.targetOperatorLevel;

  return {
    id: listing.id,
    slug: listing.slug,
    title: version.title,
    description:
      version.shortDescription || version.summary || t("noDescription"),
    iconUrl: getOperatorLevelIconSrc(level),
    operatorLevel: level ?? null,
    operatorLevelLabel:
      level != null && isMarketplaceOperatorLevel(level)
        ? t(`operatorLevel.${level}`)
        : t("operatorLevel.professional"),
    installType: resolveMarketplaceListingInstallType({
      listingIsEmployeePack: listing.isEmployeePack,
      versionIsEmployeePack: Boolean(version.isEmployeePack),
      employeePackMembers: Array.isArray(version.employeePackMembers)
        ? version.employeePackMembers
        : [],
    }),
    tags: [...new Set(tags)],
    services: marketplaceVersionServiceNames(version),
    favorites: listing._count.favorites,
    onboarded: listing._count.onboardingRecords,
    publisher: {
      name:
        version.organizationDisplayName ||
        listing.publisherOrgKey ||
        t("publishedByCreator"),
      imageUrl: getUserImgSrc(listing.publisher?.image?.id) ?? null,
      initials: publisherAvatarInitials(listing.publisher),
    },
  };
}

const promptVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 12,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: "easeOut",
    },
  },
};

export default function MarketPlace() {
  const { t } = useTranslation(["marketing", "chat"]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const loaderData = useLoaderData<typeof loader>();
  const searchFromUrl = searchParams.get("search") ?? "";
  const [searchQuery, setSearchQuery] = useState(searchFromUrl);
  /** Signed-out landing chat draft — send / Enter opens login dialog. */
  const [chatDraft, setChatDraft] = useState("");
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const displayCategory = useCallback(
    (value: string) => {
      const key = getMarketplaceCategoryTranslationKey(value);
      return key ? t(key) : value;
    },
    [t],
  );
  const lastSubmittedSearch = useRef(searchFromUrl);
  const isHomePage =
    location.pathname === "/marketplace" ||
    location.pathname === "/marketplace/";
  const selectedCategorySlugs = searchParams.getAll("category");
  const selectedOperatorLevels =
    parseOperatorLevelsFromSearchParams(searchParams);
  const employeePackOnly = searchParams.get("employeePack") === "1";
  const hasMarketplaceViewParams =
    searchParams.has("search") ||
    searchParams.getAll("category").length > 0 ||
    searchParams.getAll("level").length > 0 ||
    searchParams.get("employeePack") === "1" ||
    (searchParams.has("page") && searchParams.get("page") !== "1");
  const tabParam = searchParams.get("tab");
  const landingTab =
    tabParam === "chat"
      ? "chat"
      : tabParam === "marketplace" || hasMarketplaceViewParams
        ? "marketplace"
        : "chat";
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    selectedCategorySlugs.length > 0 ||
    selectedOperatorLevels.length > 0 ||
    employeePackOnly;
  const [categoryFiltersOpen, setCategoryFiltersOpen] = useState(false);
  const [overflowTipOpen, setOverflowTipOpen] = useState(false);

  /** First four facets are the default chips; sidebar picks replace them. */
  const MAX_CATEGORY_CHIPS = 4;
  const categoryFacets = loaderData.facets.categories;
  const defaultCategoryFacets = categoryFacets.slice(0, MAX_CATEGORY_CHIPS);
  const defaultCategorySlugs = new Set(
    defaultCategoryFacets.map((facet) => facet.slug),
  );
  const overflowSelectedFacets = selectedCategorySlugs
    .map((slug) => categoryFacets.find((facet) => facet.slug === slug))
    .filter(
      (facet): facet is (typeof categoryFacets)[number] =>
        !!facet && !defaultCategorySlugs.has(facet.slug),
    );
  const rowCategoryFacets: (typeof categoryFacets)[number][] = [];
  for (const facet of overflowSelectedFacets) {
    if (rowCategoryFacets.length >= MAX_CATEGORY_CHIPS) break;
    if (!rowCategoryFacets.some((row) => row.slug === facet.slug)) {
      rowCategoryFacets.push(facet);
    }
  }
  for (const facet of defaultCategoryFacets) {
    if (rowCategoryFacets.length >= MAX_CATEGORY_CHIPS) break;
    if (!rowCategoryFacets.some((row) => row.slug === facet.slug)) {
      rowCategoryFacets.push(facet);
    }
  }
  const hiddenSelectedFacets = selectedCategorySlugs
    .map((slug) => categoryFacets.find((facet) => facet.slug === slug))
    .filter(
      (facet): facet is (typeof categoryFacets)[number] =>
        !!facet && !rowCategoryFacets.some((row) => row.slug === facet.slug),
    );

  useEffect(() => {
    if (isHomePage) track("marketplace:view");
  }, [isHomePage]);

  useEffect(() => {
    if (searchFromUrl !== lastSubmittedSearch.current) {
      setSearchQuery(searchFromUrl);
      lastSubmittedSearch.current = searchFromUrl;
    }
  }, [searchFromUrl]);

  const submitSearch = useCallback(() => {
    if (!isHomePage) return;
    const trimmedQuery = searchQuery.trim();
    const currentSearch = searchParams.get("search") ?? "";
    if (trimmedQuery === currentSearch) return;

    const params = new URLSearchParams(searchParams);
    if (trimmedQuery) {
      params.set("search", trimmedQuery);
    } else {
      params.delete("search");
    }
    params.set("page", "1");
    params.set("tab", "marketplace");
    lastSubmittedSearch.current = trimmedQuery;
    navigate(`/marketplace?${params.toString()}`, { replace: true });
  }, [isHomePage, navigate, searchParams, searchQuery]);

  const limit = Number(searchParams.get("limit") || "12");

  const handlePageChange = useCallback(
    (nextPage: number, pageSize?: number) => {
      const params = new URLSearchParams(window.location.search);
      params.set("page", String(nextPage));
      params.set("limit", String(pageSize || limit));
      navigate(`/marketplace?${params.toString()}`, { replace: false });
    },
    [limit, navigate],
  );

  const handleOpen = useCallback(
    (slug: string) => {
      navigate(`/marketplace/${slug}`);
    },
    [navigate],
  );

  const handleShareLink = useCallback(
    (slug: string) => {
      const url =
        typeof window !== "undefined"
          ? `${window.location.origin}/marketplace/${slug}`
          : "";
      if (!url) return;
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(url).then(() => {
          toast.success(t("linkCopied"));
        });
      }
    },
    [t],
  );

  const openMarketplaceCategory = useCallback(
    (categorySlug: string) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "marketplace");
      params.delete("category");
      params.append("category", categorySlug);
      params.delete("search");
      params.set("page", "1");
      setSearchQuery("");
      lastSubmittedSearch.current = "";
      navigate(`/marketplace?${params.toString()}`, { replace: false });
    },
    [navigate, searchParams],
  );

  const buildMarketplaceSearchParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set("tab", "marketplace");
    return params;
  }, [location.search]);

  const handleToggleCategory = useCallback(
    (slug: string) => {
      const params = buildMarketplaceSearchParams();
      const current = params.getAll("category");
      const next = current.includes(slug)
        ? current.filter((s) => s !== slug)
        : [...current, slug];
      params.delete("category");
      next.forEach((s) => params.append("category", s));
      params.set("page", "1");
      navigate(`/marketplace?${params.toString()}`, { replace: false });
    },
    [buildMarketplaceSearchParams, navigate],
  );

  const handleToggleOperatorLevel = useCallback(
    (level: MarketplaceOperatorLevelValue) => {
      const params = buildMarketplaceSearchParams();
      const current = params.getAll("level");
      const next = current.includes(level)
        ? current.filter((l) => l !== level)
        : [...current, level];
      params.delete("level");
      next.forEach((l) => params.append("level", l));
      params.set("page", "1");
      navigate(`/marketplace?${params.toString()}`, { replace: false });
    },
    [buildMarketplaceSearchParams, navigate],
  );

  /** Drops the search box and every facet in one go. */
  const clearFilters = useCallback(() => {
    setSearchQuery("");
    lastSubmittedSearch.current = "";
    navigate("/marketplace?tab=marketplace", { replace: false });
  }, [navigate]);

  /** One facet group in the filter rail; mirrors the in-app catalog's. */
  const FacetGroup = ({
    title,
    empty,
    items,
    /** When set, selected chips show an X like Integrations categories. */
    withClearIcon = false,
  }: {
    title: string;
    empty: string;
    items: {
      key: string;
      label: string;
      count: number;
      checked: boolean;
      onToggle: () => void;
    }[];
    withClearIcon?: boolean;
  }) => (
    <div className="space-y-3 py-5 first:pt-0">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>

      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={item.onToggle}
              className={cn(
                "inline-flex items-center rounded-full border px-3 py-1.5 text-sm cursor-pointer transition-all",
                item.checked
                  ? "border-black bg-black text-white dark:bg-sky-900"
                  : "border-transparent bg-zinc-200/80 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700",
              )}
            >
              <span className="truncate">{item.label}</span>
              {withClearIcon && item.checked ? (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={t("clearFilters")}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onToggle();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      item.onToggle();
                    }
                  }}
                  className="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full hover:bg-white/20"
                >
                  <IconX size={13} />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const MarketplaceSidebar = () => (
    <div className="shrink-0">
      <div className="flex items-center flex-wrap gap-3 -mt-1">
        <h3 className="text-lg font-medium">{t("categoriesAndLevel")}</h3>

        {hasActiveFilters ? (
          <SimpleTooltip title={t("clearFilters")}>
            <Button
              variant="destructive"
              onClick={clearFilters}
              size="sm"
              className="text-xs"
            >
              <IconX />
              {t("clearFilters")}
            </Button>
          </SimpleTooltip>
        ) : null}
      </div>

      <div className="rows-sidebar mt-8">
        <FacetGroup
          title={t("aiEmployeeLevel")}
          empty={t("noLevelsFilter")}
          withClearIcon
          items={loaderData.facets.operatorLevels.map(({ level, count }) => ({
            key: level,
            label: t(`operatorLevel.${level}`),
            count,
            checked: selectedOperatorLevels.includes(level),
            onToggle: () => handleToggleOperatorLevel(level),
          }))}
        />

        <Separator />

        <FacetGroup
          title={t("categories")}
          empty={t("noCategoriesMatch")}
          withClearIcon
          items={loaderData.facets.categories.map((facet) => ({
            key: facet.id,
            label: displayCategory(facet.name),
            count: facet.count,
            checked: selectedCategorySlugs.includes(facet.slug),
            onToggle: () => {
              const selecting = !selectedCategorySlugs.includes(facet.slug);
              handleToggleCategory(facet.slug);
              // Close the filters sheet once a category is applied.
              if (selecting) setCategoryFiltersOpen(false);
            },
          }))}
        />
      </div>
    </div>
  );

  const cards = loaderData.listings
    .map((listing) => toListingCardData(listing, t))
    .filter((card): card is MarketplaceListingCardData => card !== null);

  const afterLoginRedirect = useMemo(() => {
    const trimmed = chatDraft.trim();
    return trimmed
      ? `/u/alpha/agent?prompt=${encodeURIComponent(trimmed)}`
      : "/u/alpha/agent";
  }, [chatDraft]);

  /**
   * Opens unconditionally. The composer's action buttons (attach, tools, voice)
   * are the first thing a signed-out visitor clicks, usually before typing
   * anything, so gating this on a non-empty draft left all three doing nothing.
   * `text` only carries a submitted message worth keeping for the redirect.
   */
  const openLoginDialog = useCallback(
    (text?: string) => {
      const trimmed = text?.trim();
      if (trimmed && trimmed !== chatDraft.trim()) {
        setChatDraft(trimmed);
      }
      setLoginDialogOpen(true);
    },
    [chatDraft],
  );

  const setLandingTab = useCallback(
    (tab: "chat" | "marketplace") => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", tab);
      navigate(`/marketplace?${params.toString()}`, { replace: true });
    },
    [navigate, searchParams],
  );

  const landingTabs = useMemo(
    () =>
      [
        { value: "chat" as const, label: t("nav:chat") },
        { value: "marketplace" as const, label: t("marketplace") },
      ] satisfies { value: "chat" | "marketplace"; label: string }[],
    [t],
  );

  // AFTER every hook: this route renders its own children. Returning the
  // outlet before the hooks above would change hook order when opening a
  // listing from the landing catalog.
  if (outlet && !isHomePage) {
    return outlet;
  }

  return (
    <div className="bg-background">
      <div className="flex flex-col">
        <LandingHeader
          showLogin={true}
          landingPrompt={chatDraft}
          tabs={landingTabs}
          tabValue={landingTab}
          onTabValueChange={setLandingTab}
        />

        {landingTab === "chat" ? (
          <div className="flex flex-1 flex-col items-center justify-center  py-15 pt-20 sm:pt-auto min-h-[80dvh] relative">
            <div
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `
        radial-gradient(circle at 30% 70%, rgba(173, 216, 230, 0.35), transparent 60%),
        radial-gradient(circle at 70% 30%, rgba(255, 182, 193, 0.4), transparent 60%)`,
              }}
            />

            {/* <div
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `
        linear-gradient(to right, #e2e8f0 1px, transparent 1px),
        linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)
      `,
                backgroundSize: "50px 50px",
                WebkitMaskImage:
                  "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
                maskImage:
                  "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
              }}
            /> */}

            <div
              className="
    absolute inset-0 z-0
    [--grid-color:#e2e8f0]
    dark:[--grid-color:rgba(255,255,255,0.02)]
  "
              style={{
                backgroundImage: `
      linear-gradient(to right, var(--grid-color) 1px, transparent 1px),
      linear-gradient(to bottom, var(--grid-color) 1px, transparent 1px)
    `,
                backgroundSize: "50px 50px",
                WebkitMaskImage:
                  "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
                maskImage:
                  "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
              }}
            />

            <div className="flex w-full max-w-3xl flex-col items-center px-5">
              <ChatLandingHero
                headline={t("chat:welcome.headline")}
                showLogo={false}
              />
              <motion.div
                className="w-full"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut",
                }}
              >
                <ChatComposer
                  className="w-full"
                  placeholder={t("chat:input.placeholder")}
                  value={chatDraft}
                  onValueChange={setChatDraft}
                  onSubmit={(message) => {
                    const text = (message.text ?? chatDraft).trim();
                    if (!text) return;
                    openLoginDialog(text);
                  }}
                  leftActions={
                    <>
                      <ChatComposerAction
                        icon={<IconPaperclip className="size-5" />}
                        label={t("chat:input.addContext")}
                        onClick={() => openLoginDialog()}
                      />
                      <Tooltip title={t("chat:input.manageIntegrations")}>
                        <Button
                          type="button"
                          variant="secondary"
                          className="rounded-full font-normal"
                          onClick={() => openLoginDialog()}
                        >
                          <IconBriefcase className="size-4" />
                          {t("chat:input.tools")}
                        </Button>
                      </Tooltip>
                    </>
                  }
                  rightActions={
                    <>
                      <ChatComposerAction
                        tone="outline"
                        icon={
                          <IconMicrophone
                            className="size-5"
                            strokeWidth={1.5}
                          />
                        }
                        label={t("chat:input.voiceInput")}
                        onClick={() => openLoginDialog()}
                      />
                      <ChatComposerSend disabled={!chatDraft.trim()} />
                    </>
                  }
                />
              </motion.div>
            </div>

            <Separator className="my-14 bg-zinc-100 dark:bg-zinc-900/90" />

            {loaderData.facets.categories.length > 0 ? (
              <motion.div
                className="grid w-full max-w-6xl grid-cols-1 gap-3 px-5 sm:grid-cols-2 lg:grid-cols-4"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      delay: 0.5,
                      staggerChildren: 0.06,
                    },
                  },
                }}
              >
                {loaderData.facets.categories.map((facet) => {
                  const category = displayCategory(facet.name);

                  return (
                    <motion.button
                      key={facet.id}
                      type="button"
                      onClick={() => openMarketplaceCategory(facet.slug)}
                      variants={promptVariants}
                      whileHover={{
                        y: -3,
                        transition: {
                          duration: 0.2,
                        },
                      }}
                      whileTap={{
                        scale: 0.98,
                      }}
                      className="group flex min-h-[110px] w-full cursor-pointer flex-col justify-between rounded-2xl border border-border/70 bg-card/80 p-4 text-left backdrop-blur-sm transition-colors duration-200 hover:border-border hover:bg-card hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="min-w-0 truncate text-sm font-semibold tracking-tight text-foreground">
                          {category}
                        </p>

                        <motion.div
                          className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background shadow-sm"
                          whileHover={{
                            scale: 1.08,
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 20,
                          }}
                        >
                          <IconArrowUpRight
                            size={16}
                            stroke={1.8}
                            className="text-muted-foreground transition-colors duration-200 group-hover:text-foreground"
                          />
                        </motion.div>
                      </div>

                      <p className="mt-3 line-clamp-2 text-xs leading-5 text-muted-foreground">
                        Explore {category.toLowerCase()} and discover relevant
                        apps, tools, and recommendations.
                      </p>
                    </motion.button>
                  );
                })}
              </motion.div>
            ) : null}
          </div>
        ) : (
          <div className="relative flex flex-1 flex-col pb-7">
            <section className="relative overflow-hidden py-20 px-5">
              {/* Dreamy Sky Pink Glow */}
              <div
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: `
        radial-gradient(circle at 30% 70%, rgba(173, 216, 230, 0.35), transparent 60%),
        radial-gradient(circle at 70% 30%, rgba(255, 182, 193, 0.4), transparent 60%)`,
                }}
              />

              {/* <div
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: `
        linear-gradient(to right, #e2e8f0 1px, transparent 1px),
        linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)
      `,
                  backgroundSize: "50px 50px",
                  WebkitMaskImage:
                    "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
                  maskImage:
                    "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
                }}
              /> */}
              <div
                className="
    absolute inset-0 z-0
    [--grid-color:#e2e8f0]
    dark:[--grid-color:rgba(255,255,255,0.10)]
  "
                style={{
                  backgroundImage: `
      linear-gradient(to right, var(--grid-color) 1px, transparent 1px),
      linear-gradient(to bottom, var(--grid-color) 1px, transparent 1px)
    `,
                  backgroundSize: "50px 50px",
                  WebkitMaskImage:
                    "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
                  maskImage:
                    "radial-gradient(ellipse 70% 60% at 50% 0%, #000 60%, transparent 100%)",
                }}
              />

              <div className="relative z-10 mx-auto flex max-w-3xl flex-col items-center text-center">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="text-2xl font-light capitalize tracking-[-0.02em] text-balance text-foreground xl:text-4xl"
                >
                  {t("heroTitle")}
                </motion.h2>
                <motion.div
                  className="w-full"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                >
                  <InputGroup className="flex-1 shrink-0 mt-8 h-16 w-full rounded-full px-5 p-3 bg-white shadow-xl dark:bg-zinc-900">
                    <InputGroupInput
                      placeholder={t("searchPlaceholder")}
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          submitSearch();
                        }
                      }}
                      className="text-base"
                    />
                    <InputGroupAddon align="inline-start">
                      <IconSearch className="text-muted-foreground" size={18} />
                    </InputGroupAddon>
                  </InputGroup>
                </motion.div>

                <motion.div
                  className="mt-8 flex w-full flex-wrap items-center justify-center gap-3"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: {
                      transition: {
                        delay: 0.5,
                        staggerChildren: 0.06,
                      },
                    },
                  }}
                >
                  {rowCategoryFacets.map((facet) => {
                    const displayPrompt = displayCategory(facet.name);
                    const { label, truncated } =
                      truncateMarketplaceDiscoveryLabel(displayPrompt);
                    const active = selectedCategorySlugs.includes(facet.slug);

                    const card = (
                      <motion.div
                        variants={promptVariants}
                        whileTap={{
                          scale: 0.98,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleToggleCategory(facet.slug)}
                          className={cn(
                            "inline-flex h-9 max-w-full cursor-pointer items-center rounded-full border px-3 py-1 text-xs font-medium transition-all",
                            active
                              ? "border-black bg-black text-white"
                              : "border-border bg-background hover:bg-muted",
                          )}
                        >
                          <span className="line-clamp-1 min-w-0">{label}</span>
                          {active ? (
                            <span
                              role="button"
                              tabIndex={0}
                              aria-label={t("clearFilters")}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleCategory(facet.slug);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleToggleCategory(facet.slug);
                                }
                              }}
                              className="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full hover:bg-white/20"
                            >
                              <IconX size={13} />
                            </span>
                          ) : null}
                        </button>
                      </motion.div>
                    );

                    return truncated ? (
                      <Tooltip key={facet.id} title={displayPrompt} side="top">
                        {card}
                      </Tooltip>
                    ) : (
                      <span key={facet.id}>{card}</span>
                    );
                  })}

                  {hiddenSelectedFacets.length > 0 ? (
                    <Popover
                      open={overflowTipOpen}
                      onOpenChange={setOverflowTipOpen}
                    >
                      <PopoverTrigger
                        className="inline-flex h-9 cursor-pointer items-center rounded-full border border-black bg-black px-3 py-1 text-xs font-medium text-white transition-all hover:bg-black/90"
                        aria-label={t("addedCategoriesCount", {
                          count: hiddenSelectedFacets.length,
                        })}
                      >
                        +{hiddenSelectedFacets.length}
                      </PopoverTrigger>
                      <PopoverContent
                        side="bottom"
                        align="center"
                        className="w-auto min-w-0 gap-0 rounded-xl bg-foreground px-3 py-1.5 text-xs text-background shadow-none ring-0"
                      >
                        <ul className="space-y-1 text-left">
                          {hiddenSelectedFacets.map((facet) => (
                            <li key={facet.id}>
                              {displayCategory(facet.name)}
                            </li>
                          ))}
                        </ul>
                      </PopoverContent>
                    </Popover>
                  ) : null}

                  <Sheet
                    open={categoryFiltersOpen}
                    onOpenChange={setCategoryFiltersOpen}
                  >
                    <SheetTrigger
                      render={
                        <motion.div
                          variants={promptVariants}
                          whileTap={{
                            scale: 0.98,
                          }}
                        >
                          <Button
                            variant="default"
                            aria-label={t("filters")}
                            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-black px-3 py-1 text-xs font-medium dark:bg-background dark:hover:bg-zinc-900 dark:text-sky-400"
                          >
                            <IconFiltersFilled className="size-3.5 dark:text-sky-400" />
                            More Filters
                          </Button>
                        </motion.div>
                      }
                    />

                    <SheetContent side="right" className="px-6 py-5">
                      <MarketplaceSidebar />
                    </SheetContent>
                  </Sheet>
                </motion.div>
              </div>
            </section>

            {cards.length === 0 ? (
              <EmptyState
                className="mt-10"
                title={
                  hasActiveFilters ? t("emptyNoMatch") : t("emptyNoListings")
                }
              />
            ) : (
              <div className="mt-10 max-w-7xl mx-auto px-5">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="min-w-0"
                >
                  <h2 className="text-lg font-medium">{t("listingsTitle")}</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {t("listingsSubtitle")}
                  </p>
                </motion.div>

                <motion.div
                  className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    ease: "easeOut",
                    delay: 0.15,
                  }}
                >
                  {cards.map((card) => (
                    <MarketplaceListingCard
                      key={card.id}
                      listing={card}
                      onOpen={() => handleOpen(card.slug)}
                      onShareLink={() => handleShareLink(card.slug)}
                    />
                  ))}
                </motion.div>

                <PaginationBlock
                  className="mt-6 px-0"
                  page={loaderData.pagination.page}
                  totalPages={Math.max(
                    1,
                    Math.ceil(
                      loaderData.pagination.total / loaderData.pagination.limit,
                    ),
                  )}
                  onPageChange={handlePageChange}
                  leading={t("paginationRange", {
                    from:
                      (loaderData.pagination.page - 1) *
                        loaderData.pagination.limit +
                      1,
                    to: Math.min(
                      loaderData.pagination.page * loaderData.pagination.limit,
                      loaderData.pagination.total,
                    ),
                    total: loaderData.pagination.total,
                  })}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <DialogContent className="sm:max-w-sm text-center">
          <DialogHeader>
            {/* Decorative badge, not a control: a <Button> here is focusable
                and announced as an unlabelled button. */}
            <div className="mb-3 flex justify-center">
              <span
                aria-hidden="true"
                className="flex size-10 items-center justify-center rounded-md bg-destructive/10 text-destructive"
              >
                <IconAlertTriangle />
              </span>
            </div>
            <DialogTitle>{t("loginToCreateOperatorTitle")}</DialogTitle>
            <DialogDescription>
              {t("loginToCreateOperatorDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <ProviderConnectionForm
              type="Login"
              providerName="oauth2"
              redirectTo={afterLoginRedirect}
              buttonText={t("common:login")}
              buttonClassName="flex w-full items-center justify-center rounded-full px-6 py-2 text-sm capitalize text-white transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-white/30 bg-[linear-gradient(93.53deg,#141A42_14.58%,#6577A4_110.19%)] sm:w-auto"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
